/**
 * reservaDemoStore.js — la demo EN VIVO de la reserva en el móvil del lead (simulador de autoescuelas, 6/10/2026).
 * Ficha: docs/features/simulador-autoescuela-reserva-movil-cobro.md
 *
 * Desde «Simular clase» de la Planificación, el closer manda al móvil del lead la plantilla «Reservar práctica»
 * (autoescuela de demo real, número de autoescuela de Conext). El lead reserva en su móvil y aquí se ve en vivo:
 *  - enviarReservaDemo → op «reserva_demo»;
 *  - cada 4 s, op «estado_demo» (las prácticas del lead y su pago) mientras dure la sesión del simulador;
 *  - simularCobro → op «pago_demo» (la alternativa a que el lead meta la tarjeta 4242) y una consulta inmediata.
 * Todo por POST /ventas/simulator-send-practica-semana (la API Gateway está llena: misma ruta, otra `op`).
 *
 * Lo que llega se refleja UNA sola vez (por practica_id): la práctica se ofrece a la Planificación
 * (practicasPlanificacion), en la cuenta del alumno del lead se apunta el cargo de la práctica y, al pagarse, el
 * pago «por WhatsApp» (autoescuelaBillingStore, con `ref` = practica_id).
 *
 * Singleton de módulo con subscribe/getState/reset. `reset` (desde resetSimDemoSession, al salir del simulador)
 * para el polling. La API se inyecta en los tests con setReservaDemoApi.
 */
import realAxiosClient from "../../api/axiosClient";
import { SECTOR_DATA_MAP } from "../../mockData";
import { addMovimiento, findMovimientoByRef } from "./autoescuelaBillingStore";
import { normalizeLeadPhone } from "./leadPhoneStore";

export const RUTA_DEMO = "/ventas/simulator-send-practica-semana";
export const POLL_MS = 4000;
export const PRECIO_PRACTICA_CENTS = 3000;
// El alumno del lead en el simulador (= DAVID_ALUMNO_ID de simAlumnosStore, que lo pone con el nombre y el
// móvil del lead). Aquí sin importarlo: simAlumnosStore importa este store para pararlo al salir.
export const LEAD_ALUMNO_ID = "alu-david-prueba";
// La autoescuela de demo tiene un profesor y un coche: los mismos que inst-001 y veh-001 del simulador.
export const PROFESOR_DEMO_ID = "inst-001";
export const VEHICULO_DEMO_ID = "veh-001";
export const CONCEPTO_CARGO = "Práctica reservada por WhatsApp";
export const CONCEPTO_PAGO = "Práctica pagada por WhatsApp";

let api = realAxiosClient;
/** Solo tests: sustituye el cliente HTTP (con `post`). Sin argumento vuelve al real. */
export function setReservaDemoApi(a) { api = a || realAxiosClient; }

const inicial = () => ({
  phone: "",
  envio: { estado: "idle", mensaje: "", motivo: "" },
  profesor: null,
  alumno: null,
  practicas: [],
  cobrando: {},
  cobroError: null,
});

let state = inicial();
let timer = null;
let consultando = false;
let generacion = 0; // cambia en cada reset: lo que llegue de antes se descarta
const aplicados = new Set(); // «cargo:<id>» / «pago:<id>» ya apuntados en esta sesión (aunque luego se borren a mano)
const subs = new Set();

const emit = () => subs.forEach((fn) => { try { fn(state); } catch (_) { /* noop */ } });
const setState = (patch) => { state = { ...state, ...patch }; emit(); };

export function subscribe(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}
export const getState = () => state;

/** Para el polling y deja el store como al principio (al salir del simulador). */
export function reset() {
  generacion += 1;
  if (timer) clearInterval(timer);
  timer = null;
  consultando = false;
  aplicados.clear();
  state = inicial();
  emit();
}

/** La práctica real, tal como la pinta la Planificación del simulador (profesor y coche de la demo). */
export function practicaParaPlanificacion(p, { alumnoId = LEAD_ALUMNO_ID } = {}) {
  return {
    practica_id: `real-${p.practica_id}`,
    alumno_ids: [alumnoId],
    type: p.type,
    start: p.start,
    end: p.end,
    status: p.status === "cancelada" ? "cancelada" : "pendiente",
    instructor_id: PROFESOR_DEMO_ID,
    vehicle_id: VEHICULO_DEMO_ID,
    sim_real: true,
    ...estadoConfirmacionReal(p),
  };
}

/**
 * Lo que el lead ha contestado DE VERDAD en su móvil (estado_demo), con los nombres de campo de la app real, para
 * el relleno oscuro de confirmada y la lista «Sin confirmar» (7/10/2026). Solo lo que hay: una reservada por él mismo sin avisar
 * queda «Sin avisar», como en la app. `semana_envio_at`/`notification_sent` sin hora (estado_demo no la da).
 */
export function estadoConfirmacionReal(p = {}) {
  const out = {};
  if (p.confirmed === true) out.confirmed = true;
  if (p.semana_ok_at) out.semana_ok_at = p.semana_ok_at;
  if (p.cambio_pedido_at) out.semana_cambio_pedido_at = p.cambio_pedido_at;
  if (p.origen === "semana" || p.semana_ok_at || p.cambio_pedido_at) out.semana_envio_at = true;
  if (p.origen === "asignada") out.notification_sent = true;
  return out;
}

/** Las prácticas reales del lead, listas para añadir a la Planificación. */
export function practicasPlanificacion(s = state) {
  return (s.practicas || []).map((p) => practicaParaPlanificacion(p, { alumnoId: LEAD_ALUMNO_ID }));
}

const nombreDelLead = () => {
  const lista = (SECTOR_DATA_MAP.autoescuela && SECTOR_DATA_MAP.autoescuela.mockAlumnos) || [];
  const a = lista.find((x) => x.alumno_id === LEAD_ALUMNO_ID);
  return (a && a.name) || "";
};

/** Apunta en la cuenta del alumno del lead el cargo y, si está pagada, el pago. Idempotente. */
function reflejarEnCuenta(practicas) {
  for (const p of practicas || []) {
    const id = p && p.practica_id;
    if (!id) continue;
    const importe = Number(p.pago && p.pago.importe_cents) || PRECIO_PRACTICA_CENTS;
    const kCargo = `cargo:${id}`;
    if (!aplicados.has(kCargo) && p.status !== "cancelada") {
      aplicados.add(kCargo);
      if (!findMovimientoByRef(id, "cargo")) {
        addMovimiento({
          alumno_id: LEAD_ALUMNO_ID, alumno_name: nombreDelLead(), tipo: "cargo",
          concepto: CONCEPTO_CARGO, importe_cents: importe, source: "reserva_demo", ref: id,
        });
      }
    }
    const kPago = `pago:${id}`;
    if (p.pago && p.pago.status === "pagado" && !aplicados.has(kPago)) {
      aplicados.add(kPago);
      if (!findMovimientoByRef(id, "pago")) {
        addMovimiento({
          alumno_id: LEAD_ALUMNO_ID, alumno_name: nombreDelLead(), tipo: "pago",
          concepto: CONCEPTO_PAGO, importe_cents: importe, metodo: "whatsapp", source: "reserva_demo", ref: id,
          ...(p.pago.paid_at ? { fecha: String(p.pago.paid_at).slice(0, 10) } : {}),
        });
      }
    }
  }
}

/** Una consulta de estado_demo (la usa el polling; también se puede llamar a mano). */
export async function consultarEstado() {
  const phone = state.phone;
  if (!phone || consultando) return state;
  consultando = true;
  const gen = generacion;
  try {
    const data = await api.post(RUTA_DEMO, { op: "estado_demo", phone });
    if (gen !== generacion || phone !== state.phone) return state;
    const practicas = Array.isArray(data && data.practicas) ? data.practicas : [];
    reflejarEnCuenta(practicas);
    setState({ alumno: (data && data.alumno) || null, practicas });
  } catch (err) {
    // Un fallo puntual del polling no se enseña: la siguiente consulta lo arregla.
    if (typeof console !== "undefined") console.warn("[reservaDemo] estado_demo:", err && err.message);
  } finally {
    if (gen === generacion) consultando = false;
  }
  return state;
}

/** Empieza (o sigue) a consultar cada 4 s lo que reserva el lead en ese móvil. */
export function seguirDemo(phone) {
  const tel = normalizeLeadPhone(phone);
  if (!tel) return;
  if (tel !== state.phone) {
    aplicados.clear();
    setState({ phone: tel, alumno: null, practicas: [], cobrando: {}, cobroError: null });
  }
  if (!timer) timer = setInterval(() => { consultarEstado(); }, POLL_MS);
}

function errorDeEnvio(err) {
  const status = err && err.response && err.response.status;
  const data = (err && err.response && err.response.data) || {};
  if (status === 409 && data.reason === "plantilla_pendiente") return { estado: "plantilla_pendiente", mensaje: data.message || "", motivo: data.reason };
  if (status === 429) return { estado: "limite", mensaje: data.message || "", motivo: "limite" };
  if (status === 400) return { estado: "error", mensaje: data.message || "", motivo: "telefono" };
  return { estado: "error", mensaje: data.message || "", motivo: data.reason || "" };
}

/** Manda la plantilla «Reservar práctica» al móvil del lead. Devuelve el estado del envío. */
export async function enviarReservaDemo({ phone, alumnoName, negocio }) {
  const tel = normalizeLeadPhone(phone);
  if (!tel) {
    setState({ envio: { estado: "error", mensaje: "", motivo: "telefono" } });
    return state.envio;
  }
  const gen = generacion;
  setState({ envio: { estado: "enviando", mensaje: "", motivo: "" } });
  try {
    const data = await api.post(RUTA_DEMO, { op: "reserva_demo", phone: tel, alumno_name: alumnoName, business_name: negocio });
    if (gen !== generacion) return state.envio;
    setState({ envio: { estado: "enviado", mensaje: "", motivo: "" }, profesor: (data && data.profesor) || null });
    seguirDemo(tel);
  } catch (err) {
    if (gen !== generacion) return state.envio;
    setState({ envio: errorDeEnvio(err) });
  }
  return state.envio;
}

/** «Simular cobro completado» de una práctica: op pago_demo y, después, una consulta inmediata. */
export async function simularCobro(practica_id) {
  const phone = state.phone;
  if (!phone || !practica_id) return;
  const gen = generacion;
  setState({ cobrando: { ...state.cobrando, [practica_id]: true }, cobroError: null });
  try {
    const data = await api.post(RUTA_DEMO, { op: "pago_demo", phone, practica_id });
    if (gen !== generacion) return;
    if (data && data.pago) {
      const practicas = state.practicas.map((p) => (p.practica_id === practica_id ? { ...p, pago: data.pago } : p));
      reflejarEnCuenta(practicas);
      setState({ practicas });
    }
  } catch (err) {
    if (gen !== generacion) return;
    setState({ cobroError: practica_id });
  } finally {
    if (gen === generacion) {
      const { [practica_id]: _fuera, ...resto } = state.cobrando;
      setState({ cobrando: resto });
    }
  }
  if (gen === generacion) await consultarEstado();
}
